import { CardButton } from "components/design-system/CardButton";
import { CreditCardData } from "data/shared/CreditCardData";
import { useAllSpendKeys } from "data/shared/hooks/useBestEarnRates";
import { useMonthlySpendState } from "data/shared/hooks/useMonthlySpendState";
import _ from "lodash";
import { useMemo, useState, memo, useCallback } from "react";

function getAllKeysWithPath(obj: any, prefix = '') {
    // Build the full key path (e.g., "b.d" or "b.d.e")
    let result: string[] = []
    // console.log('obj', obj)
    const shallowValues = Object.keys(obj)
    for (const key of shallowValues) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      // console.log('fullKey', fullKey)
      result.push(fullKey);
      // If the value is a plain object, recurse with the current path as the prefix
      if (_.isPlainObject(obj[key])) {
        result = result.concat(getAllKeysWithPath(obj[key], fullKey));
      }
    }
    return result;
}

// First, let's extract the header row to its own component
const TableHeader = memo(({ allCardKeys, includedCardsCount }: { 
  allCardKeys: string[], 
  includedCardsCount: number 
}) => (
  <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1000, boxShadow: '0 5px 5px rgba(0, 0, 0, 0.1)' }}>
    <tr>
      <th className="border" style={{ position: 'sticky', left: 0, backgroundColor: 'white' }}>
        Include?<br />
        {includedCardsCount} of {Object.values(CreditCardData).length}
      </th>
      <th className="border " style={{ position: 'sticky', left: 0, backgroundColor: 'white' }}>Info</th>
      <th className="border">Learn more link</th>
      <th className="border">Apply link</th>
      <th className="border ">ID</th>
      <th className="border ">Rewards Type</th>
      <th className="border ">Rewards Earn Rates</th>
      {allCardKeys.map((key) => (
        <th key={key} className="border ">{key}</th>
      ))}
    </tr>
  </thead>
))

// Optimize the cell rendering with a memoized component
const EarnRateCell = memo(({ value }: { value: any }) => {
  if (_.isPlainObject(value) || !value) {
    return <td className="border" style={{ color: '#86A1B3'}}>null</td>
  }
  
  const bgColor = value > 4 ? 'red' : value > 3 ? 'orange' : value > 2 ? 'yellow' : 'inherit'
  return <td className="border" style={{ backgroundColor: bgColor }}>{value}</td>
})

// Further optimize the TableRow component
const TableRow = memo(({ 
  card, 
  allCardKeys, 
  isIncluded, 
  onToggle 
}: { 
  card: typeof CreditCardData[keyof typeof CreditCardData], 
  allCardKeys: string[],
  isIncluded: boolean,
  onToggle: (id: string) => void
}) => {
  // Memoize the earn rates calculations
  const earnRateCells = useMemo(() => {
    if (!card?.rewards_earn_rates) return null
    return allCardKeys.map((key) => {
      const value = _.get(card.rewards_earn_rates, key)
      return <EarnRateCell key={key} value={value} />
    })
  }, [card.rewards_earn_rates, allCardKeys])

  return (
    <tr>
      <td className="border">
        <input 
          type="checkbox" 
          checked={isIncluded} 
          onChange={() => onToggle(card.id)}
        />
      </td>
      <td className="border " style={{ position: 'sticky', left: 0, backgroundColor: 'white' }}>
        <CardButton
          cardData={CreditCardData[card.id]}
          onClick={() => {}}
        />
      </td>
      <td className="border ">{
        card.learn_more_link ? <a href={card.learn_more_link} target="_blank" rel="noopener noreferrer">Learn more</a> : null
      }</td>
      <td className="border ">{
        card.apply_link ? <a href={card.apply_link} target="_blank" rel="noopener noreferrer">Apply link</a> : null
      }</td>
      <td className="border ">{card.id}</td>

      <td className="border ">{card.rewardsType}</td>
      <td className="border ">{card.rewardsSummary}</td>
      {earnRateCells}
    </tr>
  )
}, (prevProps, nextProps) => {
  // Custom comparison function for memo
  return prevProps.isIncluded === nextProps.isIncluded &&
         prevProps.card.id === nextProps.card.id &&
         _.isEqual(prevProps.allCardKeys, nextProps.allCardKeys)
})

export const AllCards = () => {
  const [monthlySpend] = useMonthlySpendState(null, () => {});
  const allSpendKeys = useAllSpendKeys(monthlySpend)
  
  // Memoize all expensive calculations
  const allCardKeys = useMemo(() => {
    return [...allSpendKeys, ...getAllKeysWithPath(_.merge({}, ...Object.values(CreditCardData).map((card) => card?.rewards_earn_rates)))]
  }, [allSpendKeys])

  const sortedCards = useMemo(() => {
    return Object.values(CreditCardData).sort((a, b) => a.name.localeCompare(b.name))
  }, [])

  const [includedCards, setIncludedCards] = useState<Record<string, boolean>>({})
  
  const includedCardsCount = useMemo(() => {
    return Object.values(includedCards).filter(Boolean).length
  }, [includedCards])

  const handleCheckboxChange = useCallback((cardId: string) => {
    setIncludedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }))
  }, [])

  // Virtualize the table if possible
  return (
    <div className="">
      <table className="w-full border-collapse border">
        <TableHeader 
          allCardKeys={allCardKeys}
          includedCardsCount={includedCardsCount}
        />
        <tbody>
          {sortedCards.map((card) => (
            <TableRow 
              key={card.id} 
              card={card} 
              allCardKeys={allCardKeys}
              isIncluded={!!includedCards[card.id]}
              onToggle={handleCheckboxChange}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};
